import React, { useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import * as Icons from 'lucide-react';

const ImageEditorModal = ({ isOpen, imageSrc, onClose, onSave, isSaving }) => {
    if (!isOpen) return null;

    const canvasRef = useRef(null);
    const containerRef = useRef(null);

    // Tools: 'brush', 'arrow', 'rect', 'text', 'move'
    const [activeTool, setActiveTool] = useState('brush');
    const [isDrawing, setIsDrawing] = useState(false);

    // Style State
    const [color, setColor] = useState('#ef4444'); // Default red
    const [lineWidth, setLineWidth] = useState(4);

    // Update font size of selected text when slider changes
    const handleSizeChange = (e) => {
        const val = parseInt(e.target.value);
        setLineWidth(val);

        if (selectedTextId) {
            setTextElements(prev => prev.map(t => {
                if (t.id === selectedTextId) {
                    return { ...t, fontSize: val * 6 + 12 };
                }
                return t;
            }));
        }
    };

    // Update color of selected text when color changes
    const handleColorChange = (newColor) => {
        setColor(newColor);
        if (selectedTextId) {
            setTextElements(prev => prev.map(t => {
                if (t.id === selectedTextId) {
                    return { ...t, color: newColor };
                }
                return t;
            }));
        }
    };

    // History
    const [history, setHistory] = useState([]);
    const [historyStep, setHistoryStep] = useState(-1);

    // Drawing Refs
    const startPosRef = useRef({ x: 0, y: 0 });
    const snapshotRef = useRef(null); // For shape previews

    // Text Overlay State
    const [textElements, setTextElements] = useState([]); // [{id, x, y, width, height, text, color, fontSize}]
    const [selectedTextId, setSelectedTextId] = useState(null);
    const [isDraggingText, setIsDraggingText] = useState(false);
    const [isResizingText, setIsResizingText] = useState(false);
    const dragOffsetRef = useRef({ x: 0, y: 0 });
    const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 });

    // Color Picker State
    const [showColorPicker, setShowColorPicker] = useState(false);

    // Initialize Canvas
    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = imageSrc;

        img.onload = () => {
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            ctx.drawImage(img, 0, 0);
            saveHistory();
        };
    }, [imageSrc]);

    const saveHistory = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const newHistory = [...history.slice(0, historyStep + 1), canvas.toDataURL()];
        setHistory(newHistory);
        setHistoryStep(newHistory.length - 1);
    };

    const undo = () => {
        if (historyStep > 0) {
            const step = historyStep - 1;
            loadHistory(step);
            setHistoryStep(step);
        }
    };

    const redo = () => {
        if (historyStep < history.length - 1) {
            const step = historyStep + 1;
            loadHistory(step);
            setHistoryStep(step);
        }
    };

    const loadHistory = (step) => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.src = history[step];
        img.onload = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);
        };
    };

    const getCanvasPoint = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();

        // Handle both mouse and touch
        const clientX = e.clientX || (e.touches ? e.touches[0].clientX : 0);
        const clientY = e.clientY || (e.touches ? e.touches[0].clientY : 0);

        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        return {
            x: (clientX - rect.left) * scaleX,
            y: (clientY - rect.top) * scaleY
        };
    };

    const getScreenPoint = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();

        const clientX = e.clientX || (e.touches ? e.touches[0].clientX : 0);
        const clientY = e.clientY || (e.touches ? e.touches[0].clientY : 0);

        return {
            x: clientX - rect.left,
            y: clientY - rect.top
        };
    };

    // --- DRAWING LOGIC ---

    const startDrawing = (e) => {
        // If clicking a text element or handle, don't start drawing lines
        if (e.target.dataset.type === 'text-overlay' || e.target.dataset.type === 'text-resize') return;

        // If we were editing text, clear selection unless clicking same text
        if (selectedTextId && e.target.dataset.id !== selectedTextId) {
            setSelectedTextId(null);
        }

        const point = getCanvasPoint(e);
        const screenPoint = getScreenPoint(e);

        if (activeTool === 'text') {
            const id = Date.now().toString();
            const newText = {
                id,
                x: screenPoint.x,
                y: screenPoint.y,
                width: 200, // Default width
                height: 100, // Default height
                text: 'Double click to edit',
                color: color,
                fontSize: lineWidth * 6 + 12
            };
            setTextElements([...textElements, newText]);
            setSelectedTextId(id);
            setActiveTool('move');
            return;
        }

        if (activeTool === 'move') return;

        startPosRef.current = point;
        setIsDrawing(true);

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        // Styles
        ctx.strokeStyle = color;
        ctx.fillStyle = color;
        ctx.lineWidth = lineWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        if (activeTool === 'brush') {
            ctx.beginPath();
            ctx.moveTo(point.x, point.y);
        } else {
            // For shapes, save snapshot
            snapshotRef.current = ctx.getImageData(0, 0, canvas.width, canvas.height);
        }
    };

    const draw = (e) => {
        if (!isDrawing) return;
        const point = getCanvasPoint(e);
        const ctx = canvasRef.current.getContext('2d');

        if (activeTool === 'brush') {
            ctx.lineTo(point.x, point.y);
            ctx.stroke();
        } else if (['rect', 'arrow'].includes(activeTool)) {
            // Restore snapshot to clear previous frame of shape
            if (snapshotRef.current) {
                ctx.putImageData(snapshotRef.current, 0, 0);
            }

            const startX = startPosRef.current.x;
            const startY = startPosRef.current.y;

            ctx.beginPath();
            if (activeTool === 'rect') {
                const w = point.x - startX;
                const h = point.y - startY;
                ctx.strokeRect(startX, startY, w, h);
            } else if (activeTool === 'arrow') {
                drawArrow(ctx, startX, startY, point.x, point.y);
            }
        }
    };

    const drawArrow = (ctx, fromX, fromY, toX, toY) => {
        // Dynamic head size with minimum robustness
        const headLength = Math.max(lineWidth * 5, 20);
        const dx = toX - fromX;
        const dy = toY - fromY;
        const angle = Math.atan2(dy, dx);

        // Calculate the point where the shaft should join the head (the concave recess)
        // This prevents the line stroke from poking through the tip
        const recessLength = headLength * 0.6;
        const shaftTipX = toX - recessLength * Math.cos(angle);
        const shaftTipY = toY - recessLength * Math.sin(angle);

        // Draw Shaft (Stop at the recess point)
        ctx.beginPath();
        ctx.moveTo(fromX, fromY);
        ctx.lineTo(shaftTipX, shaftTipY);
        ctx.stroke();

        // Draw Swept-back Head
        ctx.beginPath();
        ctx.moveTo(toX, toY); // Tip

        // Wing 1
        ctx.lineTo(
            toX - headLength * Math.cos(angle - Math.PI / 6),
            toY - headLength * Math.sin(angle - Math.PI / 6)
        );

        // Concave Center (connection point)
        ctx.lineTo(shaftTipX, shaftTipY);

        // Wing 2
        ctx.lineTo(
            toX - headLength * Math.cos(angle + Math.PI / 6),
            toY - headLength * Math.sin(angle + Math.PI / 6)
        );

        ctx.closePath();
        ctx.fillStyle = color;
        ctx.fill();
    };

    const stopDrawing = () => {
        if (isDrawing) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            if (activeTool === 'brush') {
                ctx.closePath();
            }
            setIsDrawing(false);
            snapshotRef.current = null;
            saveHistory();
        }
    };

    // --- TEXT HANDLING (Draggable & Resizable) ---

    const handleTextDragStart = (e, id) => {
        if (activeTool !== 'move' && activeTool !== 'text') return;
        e.stopPropagation();
        setSelectedTextId(id);

        const canvas = canvasRef.current;
        const rect = canvas?.getBoundingClientRect();
        const clientX = e.clientX || (e.touches ? e.touches[0].clientX : 0);
        const clientY = e.clientY || (e.touches ? e.touches[0].clientY : 0);

        // Check if resizing
        if (e.target.dataset.type === 'text-resize') {
            setIsResizingText(true);
            const el = textElements.find(t => t.id === id);
            resizeStartRef.current = {
                x: clientX,
                y: clientY,
                width: el.width || 200,
                height: el.height || 100
            };
        } else {
            setIsDraggingText(true);
            const el = textElements.find(t => t.id === id);
            // Calculate offset from click position (canvas-relative) to element position (also canvas-relative)
            // This keeps everything in the same coordinate system
            const clickRelativeX = rect ? clientX - rect.left : clientX;
            const clickRelativeY = rect ? clientY - rect.top : clientY;
            dragOffsetRef.current = { x: clickRelativeX - el.x, y: clickRelativeY - el.y };
        }
    };

    const handleTextDragMove = (e) => {
        const clientX = e.clientX || (e.touches ? e.touches[0].clientX : 0);
        const clientY = e.clientY || (e.touches ? e.touches[0].clientY : 0);

        // Get canvas rect to convert to canvas-relative coordinates
        const canvas = canvasRef.current;
        const rect = canvas?.getBoundingClientRect();

        if (isResizingText && selectedTextId) {
            const dx = clientX - resizeStartRef.current.x;
            const dy = clientY - resizeStartRef.current.y;

            setTextElements(prev => prev.map(t => {
                if (t.id === selectedTextId) {
                    return {
                        ...t,
                        width: Math.max(50, resizeStartRef.current.width + dx),
                        height: Math.max(30, resizeStartRef.current.height + dy)
                    };
                }
                return t;
            }));
        } else if (isDraggingText && selectedTextId && rect) {
            // Convert to canvas-relative coordinates
            const relativeX = clientX - rect.left - dragOffsetRef.current.x;
            const relativeY = clientY - rect.top - dragOffsetRef.current.y;

            setTextElements(prev => prev.map(t => {
                if (t.id === selectedTextId) {
                    return {
                        ...t,
                        x: relativeX,
                        y: relativeY
                    };
                }
                return t;
            }));
        }
    };

    const handleTextDragEnd = () => {
        setIsDraggingText(false);
        setIsResizingText(false);
    };

    const updateTextContent = (id, newText) => {
        setTextElements(prev => prev.map(t => t.id === id ? { ...t, text: newText } : t));
    };

    const deleteSelectedText = () => {
        if (selectedTextId) {
            setTextElements(prev => prev.filter(t => t.id !== selectedTextId));
            setSelectedTextId(null);
        }
    };

    // Helper to wrap text on Canvas (matches textarea behavior)
    const wrapText = (ctx, text, x, y, maxWidth, lineHeight) => {
        // Split by newlines first to respect explicit line breaks
        const paragraphs = text.split('\n');

        let currentY = y;

        paragraphs.forEach(paragraph => {
            const words = paragraph.split(' ');
            let line = '';

            for (let n = 0; n < words.length; n++) {
                const testLine = line + words[n] + ' ';
                const metrics = ctx.measureText(testLine);
                const testWidth = metrics.width;

                if (testWidth > maxWidth && n > 0) {
                    ctx.fillText(line, x, currentY);
                    line = words[n] + ' ';
                    currentY += lineHeight;
                } else {
                    line = testLine;
                }
            }
            // Draw the last line of the paragraph
            ctx.fillText(line, x, currentY);
            currentY += lineHeight; // Move down for the next paragraph
        });
    };

    // Burn text into canvas on Save
    const handleSave = () => {
        // Render all text elements to canvas
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const canvasRect = canvas.getBoundingClientRect();

        // Calculate scale from displayed canvas size to actual pixel size
        const scaleX = canvas.width / canvasRect.width;
        const scaleY = canvas.height / canvasRect.height;

        // Save current canvas state (before text) to restore for editor view after save
        const originalState = ctx.getImageData(0, 0, canvas.width, canvas.height);

        // Draw all text elements onto the canvas
        textElements.forEach(el => {
            // The textarea has p-1 padding (4px each side in display pixels)
            // Scale the padding offset for canvas coordinates
            const paddingOffset = 4 * scaleX;

            // el.x and el.y are in displayed pixels, relative to canvas container
            // Scale them to actual canvas pixel coordinates, then add padding offset
            const canvasX = (el.x * scaleX) + paddingOffset;
            const canvasY = (el.y * scaleY) + paddingOffset;

            // Width calculation: 
            // - el.width is the full textarea container width in display pixels
            // - Subtract 8px (4px padding on each side) BEFORE scaling
            // - This gives us the actual text content width
            const displayTextWidth = (el.width || 200) - 8;
            const canvasWidth = displayTextWidth * scaleX;

            // Scale font size
            const canvasFontSize = el.fontSize * scaleX;

            // Use the same font family as the textarea for consistent rendering
            ctx.font = `bold ${canvasFontSize}px sans-serif`;
            ctx.fillStyle = el.color;
            ctx.textBaseline = 'top';

            // Handle text wrapping with newlines support
            // Line height matches the textarea's line-height: 1.2
            wrapText(ctx, el.text, canvasX, canvasY, canvasWidth, canvasFontSize * 1.2);
        });

        // Create blob from canvas with text burned in
        canvas.toBlob((blob) => {
            if (blob) {
                onSave(blob);
            }
            // Restore original canvas state (without text) so user can continue editing
            ctx.putImageData(originalState, 0, 0);
        }, 'image/png');
    };

    return ReactDOM.createPortal(
        <div
            className="fixed inset-0 z-[99999] bg-black/90 backdrop-blur-md flex flex-col animate-in fade-in duration-200"
            onMouseMove={handleTextDragMove}
            onMouseUp={handleTextDragEnd}
            onTouchMove={handleTextDragMove}
            onTouchEnd={handleTextDragEnd}
        >
            {/* Top Toolbar */}
            <div className="flex flex-wrap items-center justify-between p-1.5 sm:p-3 bg-[#1e1e1e] border-b border-white/10 gap-x-1 gap-y-2 sm:gap-2 shrink-0 z-50">

                {/* Left: Tools, Color, Size */}
                <div className="flex items-center gap-1 sm:gap-2 shrink-0">

                    {/* Tools Group */}
                    <div className="flex items-center gap-0.5 sm:gap-1 bg-white/5 p-0.5 sm:p-1 rounded-xl">
                        {[
                            { id: 'brush', icon: Icons.Activity, label: 'Brush' },
                            { id: 'arrow', icon: Icons.MoveUpRight, label: 'Arrow' },
                            { id: 'rect', icon: Icons.Square, label: 'Rectangle' },
                            { id: 'text', icon: Icons.Type, label: 'Text' },
                            { id: 'move', icon: Icons.Hand, label: 'Move/Select' },
                        ].map(tool => (
                            <button
                                key={tool.id}
                                onClick={() => setActiveTool(tool.id)}
                                className={`p-1 sm:p-2.5 rounded-lg transition-all ${activeTool === tool.id ? 'bg-white/20 text-white shadow-lg shadow-white/5' : 'text-slate-400 hover:text-white hover:bg-white/10'}`}
                                title={tool.label}
                            >
                                <tool.icon size={16} className="sm:w-5 sm:h-5 text-current" />
                            </button>
                        ))}
                    </div>

                    <div className="w-[1px] h-6 sm:h-8 bg-white/10 mx-0.5 sm:mx-1 hidden sm:block"></div>

                    {/* Color & Size Group */}
                    <div className="flex items-center gap-1 sm:gap-3 bg-white/5 sm:bg-transparent p-0.5 sm:p-0 rounded-xl sm:rounded-none">
                        {/* Color Indicator */}
                        <div className="relative">
                            <button
                                onClick={() => setShowColorPicker(!showColorPicker)}
                                className="w-6 h-6 sm:w-9 sm:h-9 rounded-full border-2 border-white/20 hover:border-white shadow-lg transition-all"
                                style={{ backgroundColor: color }}
                            />
                            {/* Color Picker Popover */}
                            {showColorPicker && (
                                <>
                                    <div className="fixed inset-0 z-10" onClick={() => setShowColorPicker(false)}></div>
                                    <div className="absolute top-full mt-3 left-0 z-20 bg-[#252525] p-4 rounded-2xl border border-white/10 shadow-2xl w-64 animate-in zoom-in-95">
                                        <div className="grid grid-cols-5 gap-2 mb-4">
                                            {['#ef4444', '#f97316', '#f59e0b', '#84cc16', '#10b981', '#06b6d4', '#3b82f6', '#8b5cf6', '#d946ef', '#f43f5e', '#ffffff', '#94a3b8', '#475569', '#1e293b', '#000000'].map(c => (
                                                <button
                                                    key={c}
                                                    onClick={() => { handleColorChange(c); setShowColorPicker(false); }}
                                                    className={`w-8 h-8 rounded-full border-2 transition-transform ${color === c ? 'border-white scale-110' : 'border-transparent hover:scale-110'}`}
                                                    style={{ backgroundColor: c }}
                                                />
                                            ))}
                                        </div>
                                        <div className="relative h-10 w-full rounded-xl overflow-hidden shadow-inner">
                                            <input
                                                type="color"
                                                value={color}
                                                onChange={(e) => handleColorChange(e.target.value)}
                                                className="absolute -top-2 -left-2 w-[120%] h-[150%] p-0 cursor-pointer border-0"
                                            />
                                        </div>
                                        <p className="text-center text-xs text-slate-500 mt-2">Click bar to pick custom</p>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="w-[1px] h-6 bg-white/10 mx-1 hidden sm:block"></div>

                        {/* Size Slider */}
                        <div className="flex items-center gap-1 sm:gap-2">
                            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider hidden sm:block">Size</span>
                            <div className="flex items-center gap-1 sm:gap-2 bg-black/20 p-1 sm:p-1.5 rounded-lg border border-white/5">
                                <input
                                    type="range"
                                    min="2"
                                    max="30"
                                    value={lineWidth}
                                    onChange={handleSizeChange}
                                    className="w-12 sm:w-24 accent-white h-1 bg-slate-600 rounded-lg appearance-none cursor-pointer"
                                />
                                <div className="hidden sm:block w-3 h-3 rounded-full bg-white transition-all transform shrink-0" style={{ width: Math.min(12, Math.max(4, lineWidth / 2)), height: Math.min(12, Math.max(4, lineWidth / 2)), backgroundColor: color }}></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right: Actions */}
                <div className="flex items-center gap-1 sm:gap-2 shrink-0 ml-auto">
                    <div className="flex items-center gap-0.5 sm:gap-1 bg-white/5 p-0.5 sm:p-1 rounded-xl">
                        <button onClick={undo} disabled={historyStep <= 0} className={`p-1 sm:p-2 rounded-lg text-slate-400 hover:text-white transition-colors ${historyStep <= 0 ? 'opacity-30' : ''}`}>
                            <Icons.Undo2 size={16} className="sm:w-5 sm:h-5" />
                        </button>
                        <button onClick={redo} disabled={historyStep >= history.length - 1} className={`p-1 sm:p-2 rounded-lg text-slate-400 hover:text-white transition-colors ${historyStep >= history.length - 1 ? 'opacity-30' : ''}`}>
                            <Icons.Redo2 size={16} className="sm:w-5 sm:h-5" />
                        </button>
                    </div>

                    <div className="w-[1px] h-6 sm:h-8 bg-white/10 mx-0.5 sm:mx-2 hidden sm:block"></div>

                    <button onClick={onClose} className="p-1 sm:p-2 hover:bg-white/10 rounded-lg text-slate-400 hover:text-white transition-colors">
                        <Icons.X size={18} className="sm:w-5 sm:h-5" />
                    </button>

                    {/* Download Button */}
                    <button
                        onClick={() => {
                            const canvas = canvasRef.current;
                            const ctx = canvas.getContext('2d');
                            const canvasRect = canvas.getBoundingClientRect();
                            const scaleX = canvas.width / canvasRect.width;
                            const scaleY = canvas.height / canvasRect.height;

                            // Save original state
                            const originalState = ctx.getImageData(0, 0, canvas.width, canvas.height);

                            // Burn text to canvas for download
                            textElements.forEach(el => {
                                const paddingOffset = 4 * scaleX;
                                const canvasX = (el.x * scaleX) + paddingOffset;
                                const canvasY = (el.y * scaleY) + paddingOffset;
                                const displayTextWidth = (el.width || 200) - 8;
                                const canvasWidth = displayTextWidth * scaleX;
                                const canvasFontSize = el.fontSize * scaleX;

                                ctx.font = `bold ${canvasFontSize}px sans-serif`;
                                ctx.fillStyle = el.color;
                                ctx.textBaseline = 'top';
                                wrapText(ctx, el.text, canvasX, canvasY, canvasWidth, canvasFontSize * 1.2);
                            });

                            // Create download link
                            const link = document.createElement('a');
                            link.download = `edited_${Date.now()}.png`;
                            link.href = canvas.toDataURL('image/png');
                            link.click();

                            // Restore canvas state
                            ctx.putImageData(originalState, 0, 0);
                        }}
                        className="ml-1 sm:ml-2 px-2 sm:px-4 py-1.5 sm:py-2 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl text-xs sm:text-sm transition-all flex items-center gap-1 sm:gap-2 border border-white/20"
                    >
                        <Icons.Download size={14} className="sm:w-4 sm:h-4" />
                        <span className="hidden sm:inline">Download</span>
                    </button>

                    {/* Save Button - Solid Sky Blue */}
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="ml-1 sm:ml-2 px-2 sm:px-5 py-1.5 sm:py-2 bg-sky-500 hover:bg-sky-400 text-white font-bold rounded-xl text-xs sm:text-sm transition-all flex items-center gap-1 sm:gap-2"
                    >
                        {isSaving ? <Icons.Loader2 size={14} className="animate-spin sm:w-4 sm:h-4" /> : <Icons.Save size={14} className="sm:w-4 sm:h-4" />}
                        <span className="hidden sm:inline">Save</span>
                    </button>
                </div>
            </div>

            {/* Canvas Container */}
            <div
                ref={containerRef}
                className="flex-1 overflow-hidden flex items-center justify-center p-2 md:p-8 bg-black touch-none select-none"
            >
                {/* Inner wrapper - this div will match canvas size and be the relative parent for text overlays */}
                <div className="relative shadow-2xl shadow-black/50 inline-block">
                    <canvas
                        ref={canvasRef}
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                        className={`block max-w-full max-h-[70vh] md:max-h-[85vh] object-contain cursor-${activeTool === 'text' ? 'text' : activeTool === 'move' ? 'grab' : 'crosshair'}`}
                    />

                    {/* Draggable Resizable Text Overlay - positioned relative to canvas */}
                    {textElements.map(el => (
                        <div
                            key={el.id}
                            data-type="text-overlay"
                            data-id={el.id}
                            onMouseDown={(e) => handleTextDragStart(e, el.id)}
                            onTouchStart={(e) => handleTextDragStart(e, el.id)}
                            className={`absolute border group ${selectedTextId === el.id ? 'border-blue-500 bg-black/10' : 'border-transparent hover:border-white/30'}`}
                            style={{
                                left: el.x,
                                top: el.y,
                                width: el.width || 200,
                                height: el.height || 100,
                                cursor: 'grab'
                            }}
                        >
                            <textarea
                                value={el.text}
                                onChange={(e) => updateTextContent(el.id, e.target.value)}
                                onFocus={() => setSelectedTextId(el.id)}
                                className="w-full h-full bg-transparent outline-none resize-none p-1 overflow-hidden"
                                style={{
                                    color: el.color,
                                    fontSize: el.fontSize + 'px',
                                    fontFamily: 'sans-serif',
                                    fontWeight: 'bold',
                                    textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                                    lineHeight: '1.2'
                                }}
                            />

                            {/* Controls appear when selected */}
                            {selectedTextId === el.id && (
                                <>
                                    {/* Delete Button */}
                                    <button
                                        onClick={() => deleteSelectedText()}
                                        className="absolute -top-3 -right-3 bg-red-500 text-white rounded-full p-1 shadow-sm hover:scale-110 z-10"
                                    >
                                        <Icons.X size={10} />
                                    </button>

                                    {/* Resize Handle */}
                                    <div
                                        data-type="text-resize"
                                        onMouseDown={(e) => handleTextDragStart(e, el.id)}
                                        onTouchStart={(e) => handleTextDragStart(e, el.id)}
                                        className="absolute bottom-[-6px] right-[-6px] w-4 h-4 bg-white border border-blue-500 rounded-full cursor-se-resize z-10 shadow-sm"
                                    ></div>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ImageEditorModal;
